import { MEMORY_TOOL, secondsBetween } from '@k2/agents/chat/parts'
import type { AgentTurn, CardPart, TimedPart, ToolPart } from './turn'
import type { ChatView } from './view'

const MEMORY_TOOLS = new Set<string>(Object.values(MEMORY_TOOL))

type TextPart = Extract<TimedPart, { kind: 'text' }>
export type ActivityPart = Extract<TimedPart, { kind: 'reasoning' | 'tool' }>

type TurnBlock =
  | { readonly kind: 'text'; readonly id: string; readonly part: TextPart; readonly streaming: boolean }
  | { readonly kind: 'activity'; readonly id: string; readonly parts: readonly ActivityPart[] }
  | { readonly kind: 'card'; readonly id: string; readonly part: CardPart; readonly revised: boolean }
  | { readonly kind: 'own'; readonly id: string; readonly name: string; readonly parts: readonly ToolPart[] }
  | { readonly kind: 'memory'; readonly id: string; readonly part: ToolPart }

const same = (a: TurnBlock, b: TurnBlock) => {
  const before = a as Record<string, unknown>
  const after = b as Record<string, unknown>
  return Object.keys(after).every(key => {
    const was = before[key]
    const now = after[key]
    return (
      was === now ||
      (Array.isArray(was) && Array.isArray(now) && was.length === now.length && was.every((item, index) => item === now[index]))
    )
  })
}

// A corrected card is revised once a later card of its kind follows it in the turn.
const revisedCards = (parts: readonly TimedPart[]) => {
  const later = new Set<string>()
  const revised = new Set<string>()
  for (const part of [...parts].reverse()) {
    if (part.kind !== 'card') continue
    if (part.answer?.approved === false && later.has(part.card.kind)) revised.add(part.id)
    later.add(part.card.kind)
  }
  return revised
}

// Neighbouring rows fold into one block; a page's own tool takes its neighbouring calls, or all of them until another of its tools when it gathers.
export const blocksOf = (turn: AgentTurn, tools: ChatView['tools'] = {}, previous: readonly TurnBlock[] = []) => {
  const blocks: TurnBlock[] = []
  const last = turn.parts.at(-1)
  const revised = revisedCards(turn.parts)
  for (const part of turn.parts) {
    if (part.kind === 'tool' && MEMORY_TOOLS.has(part.name)) {
      blocks.push({ kind: 'memory', id: part.id, part })
      continue
    }
    const own = part.kind === 'tool' ? tools[part.name] : undefined
    if (own && part.kind === 'tool') {
      const at = own.gather ? blocks.findLastIndex(block => block.kind === 'own') : blocks.length - 1
      const before = blocks[at]
      if (before?.kind === 'own' && before.name === part.name) blocks[at] = { ...before, parts: [...before.parts, part] }
      else blocks.push({ kind: 'own', id: part.id, name: part.name, parts: [part] })
      continue
    }
    if (part.kind === 'text') {
      if (part.text.trim() || part === last)
        blocks.push({ kind: 'text', id: part.id, part, streaming: turn.status === 'running' && part === last })
      continue
    }
    if (part.kind === 'card') {
      blocks.push({ kind: 'card', id: part.id, part, revised: revised.has(part.id) })
      continue
    }
    const before = blocks.at(-1)
    if (before?.kind === 'activity') blocks[blocks.length - 1] = { ...before, parts: [...before.parts, part] }
    else blocks.push({ kind: 'activity', id: part.id, parts: [part] })
  }
  const kept = new Map(previous.map(block => [block.id, block]))
  return blocks.map(block => {
    const before = kept.get(block.id)
    return before && same(before, block) ? before : block
  })
}

// Something visibly moves while the run is under way: text arriving, or a row still working.
export const moving = (turn: AgentTurn) => {
  const last = turn.parts.at(-1)
  if (!last) return false
  if (last.kind === 'text') return last.endedAt === undefined && last.text.length > 0
  if (last.kind === 'tool') return !last.done
  if (last.kind === 'card') return last.answering === true
  return last.kind === 'reasoning' && last.endedAt === undefined
}

export const secondsOf = (parts: readonly { readonly startedAt?: number; readonly endedAt?: number; readonly seconds?: number }[]) => {
  const noted = parts.reduce((sum, part) => sum + (part.seconds ?? 0), 0)
  if (noted) return noted
  const starts = parts.flatMap(part => (part.startedAt === undefined ? [] : [part.startedAt]))
  const ends = parts.flatMap(part => (part.endedAt === undefined ? [] : [part.endedAt]))
  return starts.length && ends.length ? secondsBetween(Math.min(...starts), Math.max(...ends)) : null
}
